import { Alert } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { HolterReadOutDraftData } from '@common/validation/customControls/cardiology/holter/holterReadOutSchemas';
import { ProcessActivityReadContext } from '@frontend/components/pages/episodeCategory/carePath/process/processActivity/processActivityRead/processActivityReadContext';
import { getProcessActivityCustomControlData } from '@frontend/services/api/processActivitiesApiService';
import { useInterval } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';

export function HolterReadOutRead(): JSX.Element {
    const abortSignal = useAbortSignal();
    const { processActivity, readSections } = useDefinedContext(ProcessActivityReadContext);
    const [downloadProgress, setDownloadProgress] = useState<number | null>(0);
    const [uploadProgress, setUploadProgress] = useState<number | null>(0);
    const [readOutDone, setReadOutDone] = useState<boolean>(false);
    const [percentage, setPercentage] = useState<number>(0);
    const [statusText, setStatusText] = useState<string>('De recorder wordt uitgelezen. Koppel de recorder niet los.');
    const dateUploadedToAnalysisParty = readSections[0].items.find(
        (item) => item.key === 'dateUploadedToAnalysisParty'
    )?.value;

    useInterval(async () => {
        if (downloadProgress !== 100 && uploadProgress !== 100) {
            await continuousUpdateHolterReadOut();
        }
    }, 1000);

    useEffect(() => {
        if (downloadProgress !== null && downloadProgress < 100) {
            setPercentage(downloadProgress);
        } else if (downloadProgress === 100 && uploadProgress === null) {
            setStatusText('We versturen de ECG opname naar ons platform. Zet de computer nog niet uit.');
        } else if (downloadProgress === 100 && uploadProgress !== null && uploadProgress <= 100) {
            setPercentage(uploadProgress);
            setStatusText('We versturen de ECG opname naar ons platform. Zet de computer nog niet uit.');
        }

        if (downloadProgress === 100 && uploadProgress === 100) {
            setReadOutDone(true);
        }
    }, [downloadProgress, uploadProgress]);

    if (readOutDone || dateUploadedToAnalysisParty) {
        return (
            <Alert
                appearance="success"
                hasIcon={true}
            >
                De ECG opname is succesvol geüpload naar ons platform{' '}
                {dateUploadedToAnalysisParty ? `. Verstuurd naar de analist op ${dateUploadedToAnalysisParty}.` : ''}
            </Alert>
        );
    }

    return (
        <Alert
            appearance="info"
            isLoading={true}
        >
            {statusText}
            <Alert.Progress value={Math.floor(percentage)} />
        </Alert>
    );

    async function continuousUpdateHolterReadOut() {
        if (processActivity.id) {
            const customControlData: HolterReadOutDraftData = await getProcessActivityCustomControlData(
                processActivity.id,
                abortSignal
            );
            const downloadedProgress = Number(customControlData.downloadProgress);
            const uploadedProgress = Number(customControlData.uploadProgress);
            setDownloadProgress(downloadedProgress ?? 0);
            setUploadProgress(uploadedProgress ?? 0);
        }
    }
}
